<template>
    <section id="About" class="about-section">
        <div class="about-intro">
            <span class="section-kicker">Background &amp; achievements</span>
            <h2>A STEM pathway built for Suriname.</h2>
            <p>Codettes helps young people move from curiosity to capability through hands-on technology education, mentorship, and real projects. The programs are designed as a pathway: early exposure, foundational skills, advanced making, and confident application.</p>
        </div>

        <div class="program-grid">
            <article v-for="(program, index) in programs" :key="program.name" class="program-card">
                <div class="program-number">0{{ index + 1 }}</div>
                <span class="program-stage">{{ program.stage }}</span>
                <h3>{{ program.name }}</h3>
                <p>{{ program.description }}</p>
                <div class="program-skills">
                    <span v-for="skill in program.skills" :key="skill">{{ skill }}</span>
                </div>
            </article>
        </div>

        <div class="achievement-panel">
            <div>
                <span class="section-kicker">What this adds up to</span>
                <h3>More than workshops. A lasting maker culture.</h3>
            </div>
            <p>Across these programs, participants learn to design, build, program, test, present, and try again. Codettes has taken those opportunities beyond Paramaribo into districts including Saramacca, Coronie, Marowijne, and Commewijne, with mentorship, community partners, and sponsored access helping more young people take part.</p>
        </div>

        <div id="Impact" class="about-presentation">
            <div class="presentation-heading">
                <div>
                    <span class="section-kicker">See the full story</span>
                    <h3>Codettes in action.</h3>
                    <p>This presentation brings together the work, programs, and achievements behind the pathway above.</p>
                </div>
                <a :href="presentationUrl" target="_blank" rel="noopener noreferrer">Open full presentation <v-icon small>fas fa-external-link-alt</v-icon></a>
            </div>
            <div class="presentation-frame">
                <iframe title="Codettes impact presentation" :src="embedUrl" allowfullscreen loading="lazy"></iframe>
            </div>
        </div>
    </section>
</template>

<script>
    export default {
        name: 'CAbout',
        data: () => ({
            presentationUrl: 'https://docs.google.com/presentation/d/13d5eArILv7McZz2XqCPBF9W33Vl7wZ6XgplhXQggooA/edit?usp=sharing',
            embedUrl: 'https://docs.google.com/presentation/d/13d5eArILv7McZz2XqCPBF9W33Vl7wZ6XgplhXQggooA/embed?start=false&loop=false&delayms=5000',
            programs: [
                {
                    name: 'TechHuddle',
                    stage: 'Early STEM exposure · ages 8–14',
                    description: 'An introduction to technology through 3D modeling, basic electronics, circuitry, and digital fabrication. The Lab on Wheels brings tools such as 3D printers, laser cutters, vinyl cutters, and digital embroidery machines to children and communities.',
                    skills: ['3D modeling', 'Electronics', 'Lab on Wheels']
                },
                {
                    name: 'Codettes InnoStarter',
                    stage: 'Foundational skills · ages 12+',
                    description: 'A multi-month maker program where students build technology rather than only use it. Participants learn Arduino, electronics, CAD, 3D printing, and prototyping, then apply those skills to agricultural robots, autonomous systems, smart homes, and smart-city ideas.',
                    skills: ['Arduino', 'Prototyping', 'Real-world projects']
                },
                {
                    name: 'Codettes Bootcamp',
                    stage: 'Advanced maker training · 12 months',
                    description: 'An intensive pathway from introductory technology skills to advanced making and prototyping. Students work in the lab on their own projects and portfolios, using digital fabrication, embedded systems, microcontrollers, and manufacturing techniques.',
                    skills: ['Digital fabrication', 'Embedded systems', 'Portfolios']
                },
                {
                    name: 'Girls Tech League',
                    stage: 'Competitive application · robotics',
                    description: 'A weekend STEM and robotics competition where teams of five girls design, build, program, and present robotic rover cars. Coaches support teamwork, engineering, circuit building, laser cutting, 3D design, and Arduino programming before the final jury presentation.',
                    skills: ['Robotics', 'Engineering', 'Teamwork']
                }
            ]
        })
    }
</script>

<style scoped>
    .about-section { background: #fff; color: #24123f; padding: 88px 8% 96px; }
    .about-intro, .program-grid, .achievement-panel, .about-presentation { margin-left: auto; margin-right: auto; max-width: 1180px; }
    .section-kicker { color: #a8007c; display: block; font-size: .72rem; font-weight: 800; letter-spacing: .18em; text-transform: uppercase; }
    .about-intro h2 { font-size: clamp(2.5rem, 5vw, 5rem); letter-spacing: -.065em; line-height: .96; margin: 15px 0 20px; max-width: 780px; }
    .about-intro > p { color: #696276; font-size: 1.12rem; line-height: 1.75; margin: 0; max-width: 720px; }
    .program-grid { display: grid; gap: 18px; grid-template-columns: repeat(4, minmax(0, 1fr)); margin-top: 48px; }
    .program-card { background: #fbf8fd; border: 1px solid #eee3f3; border-radius: 18px; display: flex; flex-direction: column; min-height: 390px; padding: 24px 20px 20px; position: relative; transition: box-shadow .2s ease, transform .2s ease; }
    .program-card:hover { box-shadow: 0 14px 30px rgba(103,48,141,.14); transform: translateY(-5px); }
    .program-number { color: #ce128a; font-size: .72rem; font-weight: 900; letter-spacing: .16em; }
    .program-stage { color: #7a7185; font-size: .72rem; font-weight: 800; line-height: 1.4; margin-top: 34px; text-transform: uppercase; }
    .program-card h3 { color: #3b2056; font-size: 1.35rem; letter-spacing: -.04em; margin: 10px 0 12px; }
    .program-card p { color: #696276; font-size: .9rem; line-height: 1.65; margin: 0; }
    .program-skills { display: flex; flex-wrap: wrap; gap: 6px; margin-top: auto; padding-top: 20px; }
    .program-skills span { background: #f0e3f4; border-radius: 999px; color: #67308d; font-size: .68rem; font-weight: 800; padding: 6px 9px; }
    .achievement-panel { align-items: center; background: #24123f; border-radius: 22px; color: #fff; display: grid; gap: 42px; grid-template-columns: .9fr 1.1fr; margin-top: 62px; padding: 38px 42px; }
    .achievement-panel .section-kicker { color: #f1a9db; }
    .achievement-panel h3 { font-size: clamp(1.5rem, 3vw, 2.5rem); letter-spacing: -.045em; line-height: 1.05; margin: 14px 0 0; }
    .achievement-panel p { color: #d9cfe4; line-height: 1.75; margin: 0; }
    .about-presentation { margin-top: 70px; }
    .presentation-heading { align-items: flex-end; display: flex; gap: 28px; justify-content: space-between; margin-bottom: 24px; }
    .presentation-heading h3 { color: #3b2056; font-size: clamp(1.8rem, 3vw, 2.8rem); letter-spacing: -.05em; margin: 12px 0 8px; }
    .presentation-heading p { color: #696276; line-height: 1.65; margin: 0; }
    .presentation-heading a { color: #a8007c; flex-shrink: 0; font-size: .88rem; font-weight: 800; }
    .presentation-heading a .v-icon { color: inherit !important; margin-left: 7px; }
    .presentation-frame { background: #fff; border: 1px solid #eee3f3; border-radius: 20px; box-shadow: 0 18px 45px rgba(59,32,86,.16); overflow: hidden; padding-bottom: 56.25%; position: relative; }
    .presentation-frame iframe { border: 0; height: 100%; left: 0; position: absolute; top: 0; width: 100%; }
    @media (max-width: 1000px) { .program-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); } .program-card { min-height: 330px; } }
    @media (max-width: 600px) { .about-section { padding: 64px 20px 72px; } .program-grid { grid-template-columns: 1fr; margin-top: 34px; } .program-card { min-height: 0; } .achievement-panel { display: block; margin-top: 42px; padding: 28px 24px; } .achievement-panel p { margin-top: 24px; } .about-presentation { margin-top: 52px; } .presentation-heading { align-items: flex-start; flex-direction: column; } }
</style>
